import {
  IconBuildingBank,
  IconSend,
  IconChartLine,
  IconFileInvoice,
  IconReceipt2,
  IconUsers,
} from '@tabler/icons-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import useFetchExpenseStats from 'src/Hooks/Queries/useFetchExpenseStats';
import { useCurrentAssetId } from 'src/Hooks/Shared/useCurrentAssetId';
import useOperationActions from 'src/Hooks/Shared/useOperationActions';
import { trackFinancesHomeViewed, trackFinancesTileClicked } from 'src/Services/Mixpanel/events';

import { CategoryBreakdown } from './CategoryBreakdown';
import { ComingSoonStrip } from './ComingSoonStrip';
import { ComingSoonTile } from './ComingSoonTile';
import { FinanceDashboard } from './FinanceDashboard';
import styles from './FinancesHome.module.scss';
import { Props } from './FinancesHome.types';
import { FinanceTile } from './FinanceTile';

const formatEur = (amount: number): string =>
  amount.toLocaleString('fr-FR', { maximumFractionDigits: 0 });

const FinancesHome = ({
  year,
  operationsCount,
  showTaxTile,
  onNavigate,
  onAddExpense,
  canCreate,
  assetAboveQuotas,
}: Props) => {
  const operationActions = useOperationActions('home');
  const { t } = useTranslation();
  const assetId = useCurrentAssetId();
  const { data: stats } = useFetchExpenseStats(year.toString());

  useEffect(() => {
    if (!assetId) return;
    trackFinancesHomeViewed({ asset_id: assetId, year });
  }, [assetId, year]);

  const handleTileClick = (tile: Parameters<typeof trackFinancesTileClicked>[0]['tile']) => {
    trackFinancesTileClicked({ tile });
  };

  const forecastTotal = stats?.budgetForecastTotal ?? 0;
  const spent = stats?.yearlyExpenseTotal ?? 0;
  const forecastPercent = forecastTotal > 0 ? Math.round((spent / forecastTotal) * 100) : null;

  return (
    <div className={styles.container}>
      <div className={styles.heading}>
        <h2 className={styles.whatToDo}>
          {t('expenses.home.whatToDo', 'Que voulez-vous faire ?')}
        </h2>
        <span className={styles.currentYear}>
          {t('expenses.home.currentYear', 'Exercice en cours : {{year}}', { year })}
        </span>
      </div>

      <FinanceDashboard
        year={year}
        onShowUserBalance={() => onNavigate('userBalance')}
        onShowBankAccounts={() => onNavigate('bankAccounts')}
        onPrepareBudget={() => onNavigate('forecast')}
      />

      <CategoryBreakdown year={year} />

      <div className={styles.tilesGrid}>
        <FinanceTile
          variant="large"
          icon={<IconReceipt2 size={28} />}
          title={t('expenses.tab.operations', 'Mes opérations')}
          description={t(
            'expenses.tile.operations.desc',
            "J'enregistre les dépenses et les recettes de la maison, et je répartis qui paie quoi."
          )}
          kpi={
            operationsCount != null
              ? t('expenses.home.operationsCount', {
                  count: operationsCount,
                  defaultValue_one: '{{count}} opération enregistrée',
                  defaultValue_other: '{{count}} opérations enregistrées',
                })
              : undefined
          }
          onClick={() => {
            handleTileClick('operations');
            onNavigate('operations');
          }}
          primaryAction={
            canCreate
              ? {
                  label: t('expenses.actions.addExpense', 'Ajouter une dépense'),
                  onClick: onAddExpense,
                  disabled: assetAboveQuotas,
                }
              : undefined
          }
          // The three ways to record money, straight from the hub: Alexis asked for them
          // here rather than one level down in the operations page.
          secondaryActions={
            canCreate
              ? [
                  {
                    label: t('expenses.actions.addIncome', 'Ajouter une recette'),
                    onClick: operationActions.addIncome,
                    disabled: assetAboveQuotas,
                  },
                  {
                    label: t('expenses.actions.addTransfer', 'Enregistrer un remboursement'),
                    onClick: operationActions.addTransfer,
                    disabled: assetAboveQuotas,
                  },
                ]
              : undefined
          }
        />
        <FinanceTile
          variant="large"
          icon={<IconChartLine size={28} />}
          title={t('expenses.tab.forecast', 'Budget prévisionnel')}
          description={t(
            'expenses.tile.forecast.desc',
            "Je prévois les dépenses de l'année (taxes, assurances, entretien) et je suis ce qui a été réalisé."
          )}
          kpi={
            forecastTotal > 0
              ? forecastPercent != null
                ? t(
                    'expenses.home.forecastKpiWithProgress',
                    '{{amount}} € prévus • {{percent}}% réalisé',
                    {
                      amount: formatEur(forecastTotal),
                      percent: forecastPercent,
                    }
                  )
                : t('expenses.home.forecastKpi', '{{amount}} € prévus', {
                    amount: formatEur(forecastTotal),
                  })
              : undefined
          }
          onClick={() => {
            handleTileClick('forecast');
            onNavigate('forecast');
          }}
        />
        <FinanceTile
          variant="medium"
          icon={<IconSend size={26} />}
          title={t('expenses.tab.fundCalls', 'Appels de fonds')}
          description={t(
            'expenses.tile.fundCalls.desc',
            "J'envoie à chacun ce qu'il doit, avec son détail et son PDF — la sortie du budget."
          )}
          onClick={() => {
            handleTileClick('fund_calls');
            onNavigate('fundCalls');
          }}
        />
        <FinanceTile
          variant="medium"
          icon={<IconBuildingBank size={26} />}
          title={t('expenses.tab.bankAccounts', 'Nos comptes')}
          description={t(
            'expenses.tile.bankAccounts.desc',
            'Le compte bancaire de la maison et, bientôt, les avances faites par chacun.'
          )}
          onClick={() => {
            handleTileClick('bank_accounts');
            onNavigate('bankAccounts');
          }}
        />
        {showTaxTile && (
          <FinanceTile
            variant="medium"
            icon={<IconFileInvoice size={26} />}
            title={t('expenses.tile.tax.title', 'Je prépare ma déclaration fiscale')}
            description={t(
              'expenses.tile.tax.desc',
              "Les revenus et charges de l'année, regroupés comme le demande l'administration (2044 pour une indivision, 2072 pour une SCI)."
            )}
            onClick={() => {
              handleTileClick('tax_declaration');
              onNavigate('deductibleCharges');
            }}
          />
        )}
      </div>

      <ComingSoonStrip>
        <ComingSoonTile
          icon={<IconUsers size={20} />}
          title={t('cca.title.default', 'Avances entre copropriétaires')}
          description={t(
            'expenses.tile.bankAccounts.desc',
            'Le compte bancaire de la maison et, bientôt, les avances faites par chacun.'
          )}
          featureKey="partner_current_account"
        />
        {!showTaxTile && (
          <ComingSoonTile
            icon={<IconFileInvoice size={20} />}
            title={t('expenses.tile.tax.title', 'Je prépare ma déclaration fiscale')}
            description={t(
              'expenses.tile.tax.desc',
              "Les revenus et charges de l'année, regroupés comme le demande l'administration (2044 pour une indivision, 2072 pour une SCI)."
            )}
            featureKey="tax_declaration"
          />
        )}
      </ComingSoonStrip>
    </div>
  );
};

export default FinancesHome;
